import type { CreateWindowParams, ToolType } from "../windows/windowTypes";
import type { ToolId } from "../hooks/useToolDock";

export type ToolRegistryEntry = {
  id: ToolId;
  label: string;
  window: Omit<CreateWindowParams, "type" | "title"> & {
    title: string;
    type?: ToolType; // injected automatically when used
  };
};

export const defaultToolRegistry: Record<ToolId, ToolRegistryEntry> = {
  setsquare: {
    id: "setsquare",
    label: "Set Square",
    window: {
      title: "Set Square",
      initialBounds: { width: 380, height: 380, x: 180, y: 140 },
      minWidth: 280,
      minHeight: 280,
      isFrameless: true,
      isResizable: true,
      rotationDeg: 0,
    },
  },
  tts: {
    id: "tts",
    label: "Read Aloud",
    window: {
      title: "Read Aloud",
      initialBounds: { width: 420, height: 340, x: 180, y: 180 },
      minWidth: 420,
      minHeight: 340,
    },
  },
  todo: {
    id: "todo",
    label: "Todo",
    window: {
      title: "Todo List",
      initialBounds: { width: 320, height: 360, x: 160, y: 160 },
      minWidth: 320,
      minHeight: 360,
    },
  },
  calculator: {
    id: "calculator",
    label: "Calculator",
    window: {
      title: "Calculator",
      initialBounds: { width: 300, height: 420 },
      minWidth: 300,
      minHeight: 440,
    },
  },
  periodic: {
    id: "periodic",
    label: "Periodic Table",
    window: {
      title: "Periodic Table",
      initialBounds: { width: 920, height: 600 },
      minWidth: 890,
      minHeight: 620,
    },
  },
  ruler: {
    id: "ruler",
    label: "Ruler",
    window: {
      title: "Ruler",
      initialBounds: { width: 800, height: 90, x: 100, y: 100 },
      minWidth: 200,
      minHeight: 90,
      isFrameless: true,
      isResizable: false,
      rotationDeg: 0,
    },
  },
  protractor: {
    id: "protractor",
    label: "Protractor",
    window: {
      title: "Protractor",
      initialBounds: { width: 640, height: 340, x: 120, y: 120 },
      minWidth: 320,
      minHeight: 220,
      isFrameless: true,
      isResizable: true,
      rotationDeg: 0,
    },
  },
  equation: {
    id: "equation",
    label: "Equation",
    window: {
      title: "Equation Editor",
      initialBounds: { width: 775, height: 505, x: 140, y: 140 },
      minWidth: 775,
      minHeight: 505,
    },
  },
  magnifier: {
    id: "magnifier",
    label: "Magnifier",
    window: {
      title: "Text Magnifier",
      initialBounds: { width: 220, height: 220, x: 200, y: 200 },
      minWidth: 220,
      minHeight: 220,
      isFrameless: true,
      isResizable: false,
    },
  },
  geogebra: {
    id: "geogebra",
    label: "GeoGebra",
    window: {
      title: "GeoGebra",
      initialBounds: { width: 920, height: 640, x: 160, y: 120 },
      minWidth: 720,
      minHeight: 520,
    },
  },
  dictionary: {
    id: "dictionary",
    label: "Dictionary",
    window: {
      title: "Dictionary",
      initialBounds: { width: 520, height: 520, x: 200, y: 160 },
      minWidth: 420,
      minHeight: 420,
    },
  },
  browser: {
    id: "browser",
    label: "Browser",
    window: {
      title: "Browser",
      initialBounds: { width: 1024, height: 700, x: 140, y: 120 },
      minWidth: 720,
      minHeight: 520,
    },
  },
  context7: {
    id: "context7",
    label: "Context7",
    window: {
      title: "Context7",
      initialBounds: { width: 960, height: 640, x: 160, y: 120 },
      minWidth: 720,
      minHeight: 520,
    },
  },
  askai: {
    id: "askai",
    label: "Ask AI",
    window: {
      title: "Ask AI",
      initialBounds: { width: 600, height: 420, x: 180, y: 140 },
      minWidth: 520,
      minHeight: 360,
    },
  },
  annotator: {
    id: "annotator",
    label: "Annotator",
    window: {
      title: "Annotator",
      initialBounds: { width: 380, height: 620, x: 160, y: 120 },
      minWidth: 360,
      minHeight: 500,
    },
  },
};

export const defaultToolOrder: ToolId[] = [
  "setsquare",
  "tts",
  "todo",
  "calculator",
  "periodic",
  "ruler",
  "protractor",
  "equation",
  "magnifier",
  "geogebra",
  "dictionary",
  "browser",
  "context7",
  "askai",
  "annotator",
];

export type ToolOverride = {
  label?: string;
  window?: Partial<CreateWindowParams>;
};

export type ToolsDockThemeOptions = {
  initial?: "light" | "dark" | "system";
  persist?: boolean;
  showToggle?: boolean;
};
